/* =============================================================
   Digitdeck — Smooth carousel enhancer (vanilla, no deps)
   Drag-to-scroll with inertial settle for any horizontal track
   marked [data-smooth-carousel].

   Behaviour (design-system motion.md → Carousel):
   - Drag works ANYWHERE in the track, including on top of cards/images.
   - Native image/link drag is suppressed (it would otherwise cancel the
     drag mid-gesture — this is why image-heavy carousels "wouldn't drag").
   - A press that doesn't move (< threshold) is a CLICK: links, add-to-cart,
     buttons all work normally. Only a real drag (> threshold) scrolls, and
     the click that would follow a drag is swallowed so cards don't navigate.
   - On release: project the inertial landing, snap it to the NEAREST slide
     (found via scroll-snap-align, so nesting depth doesn't matter), and glide
     there with one overdamped ease-out — no hard snap, no yank. A track with
     no snap points (e.g. the video row) just glides freely.
   - No grab/grabbing cursor. Touch left native. Respects reduced-motion.
   ============================================================= */
(function () {
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var THRESH = 5;       // px of movement before a press becomes a drag
  var INERTIA = 280;    // px of glide per (px/ms) release velocity
  var MIN_MS = 320, MAX_MS = 720;

  function enhance(track) {
    if (track.__smoothCarousel) return;
    track.__smoothCarousel = true;

    var active = false, dragging = false, pid = null;
    var startX = 0, startScroll = 0, lastX = 0, lastT = 0, vel = 0;
    var raf = null, prevSnap = '', prevBehavior = '';

    function freeze() {
      prevSnap = track.style.scrollSnapType;
      prevBehavior = track.style.scrollBehavior;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      track.style.userSelect = 'none';
    }
    function restore() {
      track.style.scrollSnapType = prevSnap || '';
      track.style.scrollBehavior = prevBehavior || '';
      track.style.userSelect = '';
    }

    function onDown(e) {
      if (e.pointerType === 'touch') return;          // native touch scroll already smooth
      if (e.button != null && e.button !== 0) return;
      active = true; dragging = false; pid = e.pointerId;
      startX = e.clientX; startScroll = track.scrollLeft;
      lastX = e.clientX; lastT = e.timeStamp; vel = 0;
      if (raf) { cancelAnimationFrame(raf); raf = null; }
    }

    function onMove(e) {
      if (!active) return;
      var dx = e.clientX - startX;
      var dt = e.timeStamp - lastT;
      if (dt > 0) { vel = (e.clientX - lastX) / dt; lastX = e.clientX; lastT = e.timeStamp; }
      if (!dragging) {
        if (Math.abs(dx) <= THRESH) return;            // still a potential click
        dragging = true;
        freeze();
        try { track.setPointerCapture(pid); } catch (_) {}
      }
      track.scrollLeft = startScroll - dx;
      e.preventDefault();
    }

    // snap targets = elements with scroll-snap-align (any nesting up to 2 deep)
    function snapItems() {
      var out = [];
      (function walk(el, depth) {
        for (var i = 0; i < el.children.length; i++) {
          var c = el.children[i];
          if (getComputedStyle(c).scrollSnapAlign !== 'none') out.push(c);
          else if (depth < 2) walk(c, depth + 1);
        }
      })(track, 0);
      return out;
    }

    function nearestSnap(proj, maxScroll) {
      var items = snapItems();
      if (!items.length) return null;                  // no snap → free glide
      var trackRect = track.getBoundingClientRect();
      var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      var best = null, bestD = Infinity;
      for (var i = 0; i < items.length; i++) {
        var snapLeft = track.scrollLeft + (items[i].getBoundingClientRect().left - trackRect.left) - pad;
        snapLeft = Math.max(0, Math.min(maxScroll, snapLeft));
        var d = Math.abs(snapLeft - proj);
        if (d < bestD) { bestD = d; best = snapLeft; }
      }
      return best;
    }

    function glideTo(target) {
      var start = track.scrollLeft, dist = target - start;
      if (Math.abs(dist) < 1) { track.scrollLeft = target; restore(); return; }
      var dur = Math.max(MIN_MS, Math.min(MAX_MS, Math.abs(dist) * 0.9));
      var t0 = null;
      function ease(p) { return 1 - Math.pow(1 - p, 4); }   // ease-out quart, no overshoot
      function frame(ts) {
        if (t0 == null) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        track.scrollLeft = start + dist * ease(p);
        if (p < 1) { raf = requestAnimationFrame(frame); }
        else { raf = null; restore(); }
      }
      raf = requestAnimationFrame(frame);
    }

    function onUp() {
      if (!active) return;
      active = false;
      if (!dragging) return;                           // was a click → leave it alone
      dragging = false;

      // swallow the click that fires right after a drag (cards are links)
      var kill = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
      track.addEventListener('click', kill, { capture: true, once: true });
      setTimeout(function () { track.removeEventListener('click', kill, true); }, 60);

      var maxScroll = track.scrollWidth - track.clientWidth;
      var proj = Math.max(0, Math.min(maxScroll, track.scrollLeft + (-vel * INERTIA)));
      var target = nearestSnap(proj, maxScroll);
      if (target == null) target = proj;
      if (REDUCED.matches) { track.scrollLeft = target; restore(); return; }
      glideTo(target);
    }

    track.addEventListener('pointerdown', onDown, { passive: true });
    track.addEventListener('pointermove', onMove, { passive: false });
    track.addEventListener('pointerup', onUp, { passive: true });
    track.addEventListener('pointercancel', onUp, { passive: true });
    // kill native image/link drag — the #1 reason image cards "wouldn't drag"
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-smooth-carousel]').forEach(enhance);
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
